// SecondCameraRecorder.jsx
import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Footer from '../layout/Footer';
import TalentTalkslogo from '../assets/TalentTalkslogo.png';
import PublicHeader from '../layout/PublicHeader';
import { useNotification } from '../NotificationContext';
import { checkSecondCameraInterviewStatus } from '../utils/api';

const SecondCameraRecorder = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { notify } = useNotification();

  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [interviewId, setInterviewId] = useState(null);
  const pollingIntervalRef = useRef(null);

  // Decode JWT token to extract interview ID
  const decodeToken = (jwtToken) => {
    try {
      const parts = jwtToken.split('.');
      if (parts.length !== 3) throw new Error('Invalid token format');

      const decoded = JSON.parse(atob(parts[1]));
      const blobName = decoded.blob_name || '';

      if (blobName.endsWith('_CAM2.webm')) {
        const id = blobName.replace('_CAM2.webm', '');
        return id;
      }
      return null;
    } catch (err) {
      console.error('Failed to decode token:', err);
      return null;
    }
  };

  useEffect(() => {
    if (!token) {
      setError("❌ No token found in URL. Please scan the QR code again.");
      setLoading(false);
      return;
    }

    const id = decodeToken(token);
    if (!id) {
      setError("❌ Invalid token format. Please scan the QR code again.");
      setLoading(false);
      return;
    }

    setInterviewId(id);
    setLoading(false);
  }, [token]);

  const initCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true
      });
      videoRef.current.srcObject = mediaStream;
      setStream(mediaStream);
      setCameraEnabled(true);
      notify('success', 'Camera enabled successfully!');
    } catch (err) {
      console.error("Camera access error:", err);
      notify('error', 'Could not access camera. Please allow camera and microphone permissions in your browser settings.');
    }
  };

  const getSession = async () => {
    if (!token) throw new Error("No token available");
    const baseUrl = process.env.REACT_APP_REST_BASE_URL || "http://localhost:8000";
    const response = await fetch(`${baseUrl}/second_camera/api/session?token=${encodeURIComponent(token)}`);
    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      throw new Error(`Session fetch failed: ${response.status} - ${errorText}`);
    }
    return await response.json();
  };

  const finalizeOnServer = async (interviewId) => {
    try {
      const baseUrl = process.env.REACT_APP_REST_BASE_URL || "http://localhost:8000";
      const response = await fetch(`${baseUrl}/second_camera/finalize-second-camera-upload/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interview_id: interviewId })
      });

      if (!response.ok) {
        notify('error', `Finalize failed: ${response.status}`);
        return null;
      }

      const data = await response.json();
      if (data.secondCameraUrl) {
        notify('success', 'Sharable video link created successfully!');
      }
      return data;
    } catch (err) {
      notify('error', 'Failed to finalize recording.');
      return null;
    }
  };

  const startRecording = async () => {
    if (uploadStatus !== 'idle') return;
    if (!token) {
      notify('error', 'No token available. Please scan the QR code again.');
      return;
    }
    if (!stream) {
      notify('error', 'Camera not enabled yet.');
      return;
    }

    let mediaRecorder;
    const chunks = [];

    try {
      mediaRecorder = new MediaRecorder(stream, { mimeType: "video/webm;codecs=vp8,opus" });
    } catch {
      mediaRecorder = new MediaRecorder(stream);
    }

    mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size) chunks.push(event.data);
    };

    mediaRecorder.onstop = async () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      setUploadStatus('uploading');

      try {
        const session = await getSession();
        const presignedPut = session.presigned_put;
        const blobName = session.blob_name || "";
        let interviewId = null;
        if (blobName.endsWith("_CAM2.webm")) {
          interviewId = blobName.replace("_CAM2.webm", "");
        }

        const uploadResponse = await fetch(presignedPut, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': 'video/webm'
          },
          body: blob
        });

        if (uploadResponse.ok) {
          notify('success', 'Upload completed!');
          if (interviewId) await finalizeOnServer(interviewId);
          setUploadStatus('success');
        } else {
          notify('error', 'Upload failed.');
          setUploadStatus('error');
        }
      } catch (err) {
        notify('error', 'Upload error!');
        setUploadStatus('error');
      } finally {
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setStream(null);
        }
        setIsRecording(false);
      }
    };

    mediaRecorder.start();
    setRecorder(mediaRecorder);
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      setRecorder(null);
    }
  };

  // Auto-enable camera when interview ID is ready
  useEffect(() => {
    if (!interviewId || cameraEnabled) return;

    const autoEnableCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: true
        });
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
        setCameraEnabled(true);
        notify('success', 'Camera enabled successfully!');
      } catch (err) {
        console.error("Camera access error:", err);
        notify('error', 'Could not access camera. Please allow camera and microphone permissions in your browser settings.');
      }
    };

    autoEnableCamera();
  }, [interviewId, cameraEnabled, notify]);


  // Poll for interview status and auto-stop when interview ends
  useEffect(() => {
    if (!interviewId || uploadStatus !== 'idle') return;

    const pollInterviewStatus = async () => {
      const result = await checkSecondCameraInterviewStatus(interviewId);

      if (result.ended && isRecording) {
        console.log('✅ Interview has ended. Auto-stopping recording...');
        notify('info', 'Interview has ended. Stopping recording and uploading...');
        stopRecording();
      }
    };

    pollingIntervalRef.current = setInterval(() => {
      pollInterviewStatus();
    }, 3000); // Poll every 3 seconds

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [interviewId, isRecording, uploadStatus, notify]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-500"></div>
        <p className="mt-3 text-sm text-gray-600">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
        <h3 className="mb-3 text-lg font-bold text-gray-800">Error</h3>
        <p className="mb-5 text-center text-gray-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-teal-600 px-5 py-2 text-sm font-medium text-white hover:bg-teal-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50">
     <PublicHeader/>

      {/* Main Content */}
      <main className="flex flex-grow flex-col items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-sm">
          <h1 className="mb-4 text-center text-xl font-bold text-gray-800">Second Camera Recorder</h1>

          {/* Video Panel - Made larger and responsive */}
          <div className="relative mb-4 w-full overflow-hidden rounded-xl bg-black aspect-w-4 aspect-h-3 sm:aspect-video">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex w-full gap-3">
            <button
              onClick={!cameraEnabled ? initCamera : isRecording ? null : startRecording}
              disabled={(cameraEnabled && isRecording) || uploadStatus !== "idle"}
              className={`flex-1 rounded-lg py-3 font-medium text-white transition-all ${
                !cameraEnabled || !isRecording
                  ? "bg-teal-600 shadow-sm hover:bg-teal-700"
                  : "cursor-not-allowed bg-gray-400"
              }`}
            >
              {!cameraEnabled ? "Enable Camera" : isRecording ? "Recording..." : "Start Recording"}
            </button>

            <button
              onClick={stopRecording}
              disabled={!isRecording || uploadStatus !== "idle"}
              className={`flex-1 rounded-lg py-3 font-medium text-white transition-all ${
                !isRecording || uploadStatus !== "idle"
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-red-600 shadow-sm hover:bg-red-700"
              }`}
            >
              Stop & Upload
            </button>
          </div>

          {/* Status Messages */}
          {uploadStatus === "uploading" && (
            <div className="mt-4 text-center">
              <div className="mb-1 inline-block h-5 w-5 animate-spin rounded-full border-2 border-teal-600"></div>
              <p className="text-sm font-medium text-gray-700">Processing your video...</p>
              <p className="mt-1 text-xs text-gray-500">Please wait a moment.</p>
            </div>
          )}

          {uploadStatus === "success" && (
            <div className="mt-4 text-center">
              <p className="text-sm font-medium text-green-600">✅ Video uploaded successfully!</p>
              <p className="mt-1 text-xs text-gray-600">You may now close this window.</p>
            </div>
          )}
        </div>
      </main>

      {/* Footer pushed to the bottom */}
      <Footer />
    </div>
  );
};

export default SecondCameraRecorder;
